import { ReactNode, useMemo, useRef, useState } from 'react';

import { InfoIcon } from 'lucide-react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router';

import fs from '@/components/atoms/Fieldset';
import NotFound from '@/components/pages/NotFound/NotFound';
import { SettingsContent } from '@/components/templates/SettingsContent/SettingsContent';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import useDesks from '@/hooks/useDesks';
import { useDirtyFormBlocker } from '@/hooks/useDirtyFormBlocker';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useRoles } from '@/hooks/useRoles';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { removeFormState } from '@/redux/forms/actions';
import { getPermissions, selectIsUserOwner } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { OptionBase } from '@/shared/types/common';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { PERMISSION_GROUPS } from '@/util/permissionCatalog';

import { NEW_ROLE_ID, useNewRoleState } from '../newRole';
import { usePublishRoleEditorSave } from '../RoleEditorSaveAction';
import { PermissionGroupRow } from './PermissionGroupRow';

const FORM_ID = 'role-permissions';

const validationError = (message: string) => {
  const error = new Error(message);
  error.name = 'FormValidationError';
  return error;
};

const InfoNote = ({ children }: { children: ReactNode }) => (
  <Alert variant="info" className="[&>svg]:text-primary">
    <InfoIcon />
    <AlertDescription className="leading-5">{children}</AlertDescription>
  </Alert>
);

export const RolePermissionsEditor = () => {
  useSegments(PageName.MANAGE_ROLE_PERMISSIONS);

  const { t } = useTranslation();
  const { role_id: roleId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { roles, listStatus: rolesStatus, createRoleAsync } = useRoles();

  const isNewRoleRoute = roleId === NEW_ROLE_ID;
  const { roleName: pendingName, sourceRoleId } = useNewRoleState(roleId);
  const isUnsaved = !!pendingName;
  const [hasTriedSave, setHasTriedSave] = useState(false);
  const environmentTriggerRef = useRef<HTMLButtonElement>(null);

  const canWrite = useSelector((state: RootState) => getPermissions(state, 'roles', actions.WRITE));
  const isUserOwner = useSelector(selectIsUserOwner);
  const slug = useSelector(selectAccountSlug);

  const role = roles.find((r) => r.role_id === roleId);
  const pendingDescription = sourceRoleId
    ? roles.find((r) => r.role_id === sourceRoleId)?.description
    : undefined;
  const hadRole = useRef(false);
  hadRole.current = hadRole.current || !!role;
  const isSystemRole = !isUnsaved && rolesStatus === 'success' && !role?.account_id;
  const isReadOnly = isSystemRole || !canWrite;

  const {
    access,
    isScopeIncomplete,
    isLoading: isLoadingPermissions,
    hasError,
    droppedOwnerOnlyLabels,
    isDirty,
    setLevel,
    setToggle,
    setConversationScope,
    setAiAgentConversations,
    setEnvironmentScope,
    setEnvironmentIds,
    save,
    saveAsync,
    isSaving,
  } = useRoleAccess(roleId, {
    isNewRole: isNewRoleRoute,
    pendingName,
    pendingDescription,
    sourceRoleId,
    createRole: createRoleAsync,
    onCreated: (createdRoleId) => {
      flushSync(() => dispatch(removeFormState({ formId: FORM_ID })));
      navigate(`/${slug}/account/manage/roles/${createdRoleId}`, { replace: true });
    },
  });

  const { desks } = useDesks();

  const environmentOptions: OptionBase[] = useMemo(
    () => (desks ?? []).map((desk) => ({ label: desk.name, value: desk.desk_id })),
    [desks]
  );

  const isLoading =
    isLoadingPermissions || ((!isUnsaved || !!sourceRoleId) && rolesStatus === 'pending');

  useTrackFormState({ isDirty, formId: FORM_ID });

  const environmentError =
    hasTriedSave && isScopeIncomplete ? t('roles.scopes.environments_select') : undefined;

  const flagIncompleteScope = () => {
    setHasTriedSave(true);
    environmentTriggerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    environmentTriggerRef.current?.focus({ preventScroll: true });
  };

  const handleSave = () => {
    if (isSaving) {
      return;
    }
    if (isScopeIncomplete) {
      flagIncompleteScope();
      return;
    }
    setHasTriedSave(false);
    save();
  };

  usePublishRoleEditorSave(isReadOnly || isLoading || !isDirty ? null : isSaving, handleSave);

  useDirtyFormBlocker(async () => {
    if (isScopeIncomplete) {
      flagIncompleteScope();
      throw validationError('The environment scope has no environment selected');
    }
    if (isSaving) {
      throw validationError('A save is already in flight');
    }
    const { failed } = await saveAsync();
    if (isUnsaved) {
      return true;
    }
    if (failed > 0) {
      throw validationError('Some role permissions failed to save');
    }
  });

  if (isNewRoleRoute && !pendingName) {
    return <Navigate to={`/${slug}/account/manage/roles`} replace />;
  }

  if (!isUnsaved && rolesStatus === 'success' && !role) {
    return hadRole.current ? (
      <Navigate to={`/${slug}/account/manage/roles`} replace />
    ) : (
      <NotFound />
    );
  }

  if (hasError || (rolesStatus === 'error' && roles.length === 0)) {
    return (
      <SettingsContent className="pt-8">
        <Alert variant="destructive">
          <InfoIcon />
          <AlertDescription className="leading-5">
            {t('roles.permissions_load_error')}
          </AlertDescription>
        </Alert>
      </SettingsContent>
    );
  }

  return (
    <SettingsContent className="pt-8 pb-24">
      <div className="flex flex-col gap-3">
        <fs.Title variant="primary">{t('roles.permissions_heading')}</fs.Title>

        {droppedOwnerOnlyLabels.length > 0 && !isLoading && (
          <InfoNote>
            {t('roles.owner_only_not_copied', {
              0: droppedOwnerOnlyLabels.map((labelKey) => t(labelKey)).join(', '),
            })}
          </InfoNote>
        )}

        <div className="flex max-w-lg flex-col gap-6 self-stretch rounded-lg border border-border bg-background px-6 py-6">
          <div className="flex flex-col items-start gap-6 self-stretch" aria-busy={isLoading}>
            {isLoading
              ? PERMISSION_GROUPS.map((group) => (
                  <Skeleton key={group.key} className="h-12 w-full" />
                ))
              : PERMISSION_GROUPS.map((group) => (
                  <PermissionGroupRow
                    key={group.key}
                    group={group}
                    level={access.levels[group.key]}
                    toggles={access.toggles}
                    conversationScope={access.conversationScope}
                    aiAgentConversations={access.aiAgentConversations}
                    environmentError={environmentError}
                    environmentTriggerRef={environmentTriggerRef}
                    environmentScope={access.environmentScope}
                    environmentIds={access.environmentIds}
                    environmentOptions={environmentOptions}
                    disabled={isReadOnly || (!!group.ownerOnly && !isUserOwner)}
                    isUserOwner={isUserOwner}
                    disabledReason={
                      group.ownerOnly && !isUserOwner ? t('roles.permissions_forbidden') : undefined
                    }
                    onLevelChange={(level) => setLevel(group.key, level)}
                    onToggleChange={setToggle}
                    onConversationScopeChange={setConversationScope}
                    onAiAgentConversationsChange={setAiAgentConversations}
                    onEnvironmentScopeChange={setEnvironmentScope}
                    onEnvironmentIdsChange={setEnvironmentIds}
                  />
                ))}
          </div>
        </div>

        {isSystemRole && !isLoading && <InfoNote>{t('roles.system_roles_read_only')}</InfoNote>}
      </div>
    </SettingsContent>
  );
};
